import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Modal } from "antd";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { useMenu } from "@/context/MenuContext";
import Icon from "@/components/Icon";

export const OPEN_PALETTE_EVENT = "open-command-palette";

const MAX_RESULTS = 50;

/**
 * Cmd/Ctrl+K — цэс, процесс хайж шууд очих.
 *
 * Замууд нь MenuContent-той ЯГ ИЖИЛ байх ёстой:
 *   admin submenu → /admin/{moduleid}/{processcode}
 *   flow typeid=3 → /pf/{linkid}
 *   бусад flow    → /si/si000201/{linkid} (жагсаалт), /si/si000301/{linkid} (үүсгэх)
 * Эрхийн шүүлтийг getMenu/getFlowMenu өөрөө хийдэг тул энд давтахгүй.
 */
const CommandPalette = () => {
    const { getMenu, getFlowMenu } = useAuth();
    const { onMenuClose } = useMenu();
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const isMn = i18n.language === "mn";

    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [active, setActive] = useState(0);
    const listRef = useRef(null);
    const inputRef = useRef(null);

    const close = useCallback(() => {
        setOpen(false);
        setQuery("");
        setActive(0);
    }, []);

    useEffect(() => {
        const onKey = (e) => {
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
                e.preventDefault();
                setOpen((v) => !v);
            }
        };
        const onOpen = () => setOpen(true);
        window.addEventListener("keydown", onKey);
        window.addEventListener(OPEN_PALETTE_EVENT, onOpen);
        return () => {
            window.removeEventListener("keydown", onKey);
            window.removeEventListener(OPEN_PALETTE_EVENT, onOpen);
        };
    }, []);

    // Цэсийг ЗӨВХӨН нээгдэх үед уншина (sessionStorage-аас).
    const entries = useMemo(() => {
        if (!open) return [];
        const out = [];
        const pick = (mn, en) => (isMn ? mn : en) || mn || en || "";
        try {
            (getMenu() ?? []).forEach((mod) => {
                (mod.submenus ?? []).forEach((sub) => {
                    out.push({
                        key: `a:${mod.moduleid}:${sub.processcode}`,
                        title: pick(sub.processname, sub.processname2),
                        group: pick(mod.name, mod.name2),
                        icon: "Settings",
                        path: `/admin/${mod.moduleid}/${sub.processcode}`,
                    });
                });
            });
        } catch (e) {
            // цэс ачаалагдаагүй бол зүгээр л хоосон
        }
        try {
            (getFlowMenu() ?? []).forEach((item) => {
                const title = pick(item.name, item.name2);
                if (item.typeid === 3 || item.typeid === "3") {
                    out.push({ key: `p:${item.linkid}`, title, group: t("processes"), icon: "Table", path: `/pf/${item.linkid}` });
                } else {
                    out.push({ key: `l:${item.linkid}`, title, group: t("list"), icon: "ArrowLeftRight", path: `/si/si000201/${item.linkid}` });
                    out.push({ key: `c:${item.linkid}`, title, group: t("create"), icon: "Plus", path: `/si/si000301/${item.linkid}` });
                }
            });
        } catch (e) {
            // мөн адил
        }
        // Урсгалын "үүсгэх" (бүртгэх) эхэнд, түүний доор жагсаалт/процесс, хамгийн сүүлд admin цэс.
        const rank = (e) => (e.key[0] === "c" ? 0 : e.key[0] === "l" || e.key[0] === "p" ? 1 : 2);
        return out
            .map((e, i) => ({ e, i }))
            .sort((a, b) => rank(a.e) - rank(b.e) || a.i - b.i)
            .map(({ e }) => e);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, isMn]);

    const results = useMemo(() => {
        const q = query.trim().toLowerCase();
        const list = q ? entries.filter((e) => `${e.title} ${e.group}`.toLowerCase().includes(q)) : entries;
        return list.slice(0, MAX_RESULTS);
    }, [entries, query]);

    useEffect(() => setActive(0), [query]);

    useEffect(() => {
        listRef.current?.querySelector(`[data-idx="${active}"]`)?.scrollIntoView({ block: "nearest" });
    }, [active]);

    const go = (entry) => {
        if (!entry) return;
        close();
        onMenuClose();
        navigate(entry.path);
    };

    const onKeyDown = (e) => {
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setActive((i) => Math.min(i + 1, results.length - 1));
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActive((i) => Math.max(i - 1, 0));
        } else if (e.key === "Enter") {
            e.preventDefault();
            go(results[active]);
        }
    };

    return (
        <Modal
            open={open}
            onCancel={close}
            footer={null}
            closable={false}
            destroyOnClose
            // antd Modal нээгдсэний дараа focus-ыг өөртөө авдаг тул autoFocus хангалтгүй.
            afterOpenChange={(o) => o && inputRef.current?.focus()}
            width={560}
            style={{ top: 96 }}
            styles={{ content: { padding: 0, overflow: "hidden" }, body: { padding: 0 } }}
            className="command-palette"
        >
            <div className="flex items-center gap-2 border-b border-slate-200 px-4 py-3">
                <Icon name="Search" size={16} color="#94a3b8" />
                <input
                    ref={inputRef}
                    autoFocus
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={onKeyDown}
                    placeholder={t("search") + "…"}
                    className="flex-1 bg-transparent text-sm outline-none placeholder:text-slate-400"
                />
                <kbd className="rounded-md border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[10px] font-medium text-slate-500">Esc</kbd>
            </div>
            <div ref={listRef} className="max-h-[360px] overflow-y-auto p-2">
                {results.length === 0 ? (
                    <div className="py-8 text-center text-sm text-slate-400">—</div>
                ) : (
                    results.map((e, i) => (
                        <div
                            key={e.key}
                            data-idx={i}
                            onMouseMove={() => setActive(i)}
                            onClick={() => go(e)}
                            className={`flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2 text-sm ${
                                i === active ? "bg-slate-100" : ""
                            }`}
                        >
                            <Icon name={e.icon} size={16} color="#64748b" />
                            <span className="flex-1 truncate">{e.title}</span>
                            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-500">{e.group}</span>
                        </div>
                    ))
                )}
            </div>
        </Modal>
    );
};

export default CommandPalette;
